import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useField } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import {
  ORIGIN_COUNTRY_CODES,
  formatOriginCountryQueryValue,
  parseOriginCountryQueryValue,
} from '../../../util/originCountry';
import getCountryCodes from '../../../translations/countryCodes';
import { CONTINENT_COUNTRY_CODES } from '../../../shared/countryData';

import { IconArrowHead } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './OriginCountryFilter.module.css';

// Same regions and labels as the shipping destinations selection in the
// listing wizard (EditListingDeliveryForm), so both UIs look and read alike.
const REGIONS = [
  { key: 'europe', labelId: 'EditListingDeliveryForm.regionEurope' },
  { key: 'northAmerica', labelId: 'EditListingDeliveryForm.regionNorthAmerica' },
  { key: 'southAmerica', labelId: 'EditListingDeliveryForm.regionSouthAmerica' },
  { key: 'australia', labelId: 'EditListingDeliveryForm.regionOceania' },
  { key: 'asia', labelId: 'EditListingDeliveryForm.regionAsia' },
  { key: 'africa', labelId: 'EditListingDeliveryForm.regionAfrica' },
];

// Continent-grouped country checkbox tree. Rendered inside the FilterForm
// (react-final-form) that FilterPlain/FilterPopup provide, so selection state
// lives in the form value under `name` (an array of ISO codes). Unlike the
// wizard's shipping destinations, an empty selection simply means "no country
// filter" — there is no worldwide shorthand.
const CountryTreeField = props => {
  const { name, locale } = props;
  const intl = useIntl();
  const { input } = useField(name, { defaultValue: [] });
  const selected = useMemo(() => new Set(input.value || []), [input.value]);

  const [expandedContinents, setExpandedContinents] = useState(() => new Set());

  const allCountries = useMemo(
    () =>
      getCountryCodes(locale)
        .map(({ code, name: countryName }) => ({ code, name: countryName }))
        .sort((a, b) => a.name.localeCompare(b.name, locale, { sensitivity: 'base' })),
    [locale]
  );

  // Only countries present in the curated list render as selectable rows.
  const continentSelectable = useMemo(() => {
    const renderable = new Set(allCountries.map(c => c.code));
    return REGIONS.reduce((acc, region) => {
      acc[region.key] = (CONTINENT_COUNTRY_CODES[region.key] || []).filter(c => renderable.has(c));
      return acc;
    }, {});
  }, [allCountries]);

  const updateSelection = nextSet => {
    input.onChange(allCountries.map(c => c.code).filter(code => nextSet.has(code)));
  };

  const toggleCountry = code => {
    const next = new Set(selected);
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    updateSelection(next);
  };

  const toggleContinent = regionKey => {
    const codes = continentSelectable[regionKey] || [];
    const allInSelected = codes.every(c => selected.has(c));
    const next = new Set(selected);
    if (allInSelected) {
      codes.forEach(c => next.delete(c));
    } else {
      codes.forEach(c => next.add(c));
    }
    updateSelection(next);
  };

  const toggleContinentExpansion = regionKey => {
    setExpandedContinents(prev => {
      const next = new Set(prev);
      if (next.has(regionKey)) {
        next.delete(regionKey);
      } else {
        next.add(regionKey);
      }
      return next;
    });
  };

  return (
    <div className={css.continentList}>
      {REGIONS.map(region => {
        const codes = continentSelectable[region.key] || [];
        if (codes.length === 0) {
          return null;
        }
        const codeSet = new Set(codes);
        const selectedCount = codes.filter(c => selected.has(c)).length;
        const allInSelected = selectedCount === codes.length;
        const partiallySelected = selectedCount > 0 && selectedCount < codes.length;
        const expanded = expandedContinents.has(region.key);
        const continentCountries = allCountries.filter(c => codeSet.has(c.code));

        return (
          <div key={region.key} className={css.continentItem}>
            <div
              className={classNames(css.continentRow, {
                [css.rowSelected]: allInSelected || partiallySelected,
              })}
            >
              <button
                type="button"
                className={css.continentExpandButton}
                onClick={() => toggleContinentExpansion(region.key)}
                aria-expanded={expanded}
              >
                <IconArrowHead
                  direction="right"
                  className={classNames(css.chevron, {
                    [css.chevronExpanded]: expanded,
                  })}
                />
                <span className={css.rowText}>
                  {intl.formatMessage({ id: region.labelId })}
                </span>
              </button>
              <label
                className={css.continentSelect}
                aria-label={intl.formatMessage({ id: region.labelId })}
              >
                <input
                  type="checkbox"
                  className={css.nativeCheckbox}
                  checked={allInSelected}
                  ref={el => {
                    if (el) el.indeterminate = partiallySelected;
                  }}
                  onChange={() => toggleContinent(region.key)}
                />
                <span
                  className={classNames(css.mark, css.markBold, {
                    [css.markChecked]: allInSelected,
                  })}
                  aria-hidden="true"
                >
                  {allInSelected ? (
                    <span className={css.markCheck}>✓</span>
                  ) : partiallySelected ? (
                    <span className={css.markDash}>–</span>
                  ) : null}
                </span>
              </label>
            </div>
            <div
              className={classNames(css.countryListWrapper, {
                [css.countryListWrapperExpanded]: expanded,
              })}
            >
              <ul className={css.countryList} aria-hidden={!expanded}>
                {continentCountries.map(c => {
                  const isChecked = selected.has(c.code);
                  return (
                    <li key={c.code} className={css.countryItem}>
                      <label
                        className={classNames(css.countryRow, {
                          [css.rowSelected]: isChecked,
                        })}
                      >
                        <input
                          type="checkbox"
                          className={css.nativeCheckbox}
                          checked={isChecked}
                          tabIndex={expanded ? 0 : -1}
                          onChange={() => toggleCountry(c.code)}
                        />
                        <span className={css.rowText}>{c.name}</span>
                        <span
                          className={classNames(css.mark, {
                            [css.markChecked]: isChecked,
                          })}
                          aria-hidden="true"
                        >
                          {isChecked ? <span className={css.markCheck}>✓</span> : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format the query param: { pub_originCountry: 'DE,AT' } (comma = "any of").
// All countries selected maps to null (no filter, no active-state badge); an
// empty selection maps to the "none" sentinel, which matches no listings.
const format = (selectedOptions, queryParamName) => {
  return { [queryParamName]: formatOriginCountryQueryValue(selectedOptions) };
};

/**
 * OriginCountryFilter component
 *
 * A multi-select filter on the listing's country of origin
 * (publicData.originCountry). The selection UI mirrors the shipping
 * destinations picker of the listing wizard: countries are grouped by
 * continent with expandable lists and per-continent select-all checkboxes.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const OriginCountryFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // No query param means "no filter" — the form opens with every country
  // checked, so unchecking is the only gesture needed to narrow the search.
  // The "none" sentinel parses to an empty array, so a fully unchecked form
  // stays unchecked after applying.
  const selectedOptions = hasInitialValues
    ? parseOriginCountryQueryValue(initialValues[queryParamName])
    : [...ORIGIN_COUNTRY_CODES];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <CountryTreeField name={name} locale={intl.locale} />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <CountryTreeField name={name} locale={intl.locale} />
    </FilterPlain>
  );
};

export default OriginCountryFilter;
